/**
 * Hook for AI batch analysis flow.
 * Extracted from useParsingWorkflow.ts (Phase 2.4 refactoring).
 *
 * Owns: analyzing / batchProgress / queueInfo / activeBatchId /
 *       aiAnalysisSkipped / aiFilter / aiSearchText state, plus
 *       analysisPollRef interval ref.
 *
 * Provides: handleAnalyze (start fresh batch), resumeAnalysisPoll
 * (continue existing batch from server), stopAnalysisPoll (cleanup),
 * resetAnalysis (full reset for resetState/handleStartOver).
 */

import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { message } from 'antd'
import api from '@/services/api'
import { normalizeUrl } from '../utils/urlUtils'
import type { DomainWithAnalysis, AIConfig } from '../types'

export interface QueueInfo {
  position: number
  ahead_batches: number
  urls_ahead: number
  estimated_wait_minutes: number
  running_batch: { batch_id: number; total: number; processed: number } | null
}

export interface AIAnalysisFilter {
  filter: 'all' | 'approved' | 'rejected' | 'uncertain'
  searchText: string
}

interface ManualWorkflowPayload {
  project_id: number
  project_name: string
  input_method: 'manual' | 'yandex'
  current_step: number
  task_id: string
  ai_config: AIConfig | null
  ai_filter: 'all' | 'approved' | 'rejected' | 'uncertain'
  ai_search_text: string
  selected_phones: string[]
}

interface ActiveAnalysisPayload {
  batch_id: number
  project_id: number
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  total: number
  processed: number
  started_at: string
}

interface UseAIAnalysisFlowProps {
  projectId?: number | null
  projectCrmId?: number | null
  projectName?: string | null
  domains: DomainWithAnalysis[]
  setDomains: Dispatch<SetStateAction<DomainWithAnalysis[]>>
  currentStep: number
  taskId: string | null
  aiConfig: AIConfig | null
  selectedPhones: Set<string>
  setActiveAnalysis: (projectId: number, payload: ActiveAnalysisPayload) => void
  saveManualWorkflow: (projectId: number, payload: ManualWorkflowPayload) => void
  updateAnalysisProgress: (
    projectId: number,
    processed: number,
    status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  ) => void
  clearActiveAnalysis: (projectId: number) => void
}

export interface UseAIAnalysisFlowResult {
  analyzing: boolean
  batchProgress: { processed: number; total: number } | null
  queueInfo: QueueInfo | null
  activeBatchId: number | null
  aiAnalysisSkipped: boolean
  aiFilter: 'all' | 'approved' | 'rejected' | 'uncertain'
  aiSearchText: string
  setAnalyzing: Dispatch<SetStateAction<boolean>>
  setBatchProgress: Dispatch<SetStateAction<{ processed: number; total: number } | null>>
  setQueueInfo: Dispatch<SetStateAction<QueueInfo | null>>
  setActiveBatchId: Dispatch<SetStateAction<number | null>>
  setAiAnalysisSkipped: Dispatch<SetStateAction<boolean>>
  setAiFilter: Dispatch<SetStateAction<'all' | 'approved' | 'rejected' | 'uncertain'>>
  setAiSearchText: Dispatch<SetStateAction<string>>
  handleAnalyze: () => Promise<void>
  resumeAnalysisPoll: (batchId: number) => Promise<void>
  /** Stop active polling interval (idempotent) */
  stopAnalysisPoll: () => void
  /** Reset all AI analysis state (called from resetState/handleStartOver) */
  resetAnalysis: (clearFilters?: boolean) => void
  /** Internal ref to polling interval — needed by facade cleanup useEffect */
  analysisPollRef: React.MutableRefObject<NodeJS.Timeout | null>
}

export function useAIAnalysisFlow({
  projectId,
  projectCrmId,
  projectName,
  domains,
  setDomains,
  currentStep,
  taskId,
  aiConfig,
  selectedPhones,
  setActiveAnalysis,
  saveManualWorkflow,
  updateAnalysisProgress,
  clearActiveAnalysis,
}: UseAIAnalysisFlowProps): UseAIAnalysisFlowResult {
  const [analyzing, setAnalyzing] = useState(false)
  const [batchProgress, setBatchProgress] = useState<{ processed: number; total: number } | null>(null)
  const [queueInfo, setQueueInfo] = useState<QueueInfo | null>(null)
  const [activeBatchId, setActiveBatchId] = useState<number | null>(null)
  const [aiAnalysisSkipped, setAiAnalysisSkipped] = useState(false)
  const [aiFilter, setAiFilter] = useState<'all' | 'approved' | 'rejected' | 'uncertain'>('all')
  const [aiSearchText, setAiSearchText] = useState('')

  const analysisPollRef = useRef<NodeJS.Timeout | null>(null)

  const stopAnalysisPoll = useCallback(() => {
    if (analysisPollRef.current) {
      clearInterval(analysisPollRef.current)
      analysisPollRef.current = null
    }
  }, [])

  // Latest values used inside long-lived callbacks
  const aiFilterRef = useRef(aiFilter)
  aiFilterRef.current = aiFilter
  const aiSearchTextRef = useRef(aiSearchText)
  aiSearchTextRef.current = aiSearchText

  const handleAnalyze = useCallback(async () => {
    if (!projectId) {
      message.error('Проект не выбран')
      return
    }

    // Idempotency guard: don't start a duplicate poll
    if (analysisPollRef.current) {
      console.warn('[Workflow] Analysis polling уже запущен, пропускаем дубль')
      return
    }

    try {
      setAnalyzing(true)

      const selectedDomains = domains.filter((d) => d.selected)

      if (selectedDomains.length === 0) {
        message.warning('Нет выбранных доменов для анализа')
        setAnalyzing(false)
        return
      }

      const pureDomains = selectedDomains
        .filter((d) => d.source_type === 'domain')
        .map((d) => d.full_url || `https://${d.host}/`)

      const pagesWithPaths = selectedDomains
        .filter((d) => d.source_type === 'url')
        .map((d) => d.full_url || `https://${d.host}/`)

      const uniqueDomains = Array.from(
        new Set(pureDomains.map((url) => url.toLowerCase().replace(/\/$/, '')))
      )
      const uniqueUrls = Array.from(
        new Set(pagesWithPaths.map((url) => url.toLowerCase().replace(/\/$/, '')))
      )

      console.log(`📊 AI Analysis: ${uniqueDomains.length} domains + ${uniqueUrls.length} URLs`)
      message.info(`🚀 Запуск AI анализа: ${uniqueDomains.length} доменов + ${uniqueUrls.length} URL...`)

      const batchResponse = await api.startBatchAnalysis({
        domains: uniqueDomains,
        urls: uniqueUrls.length > 0 ? uniqueUrls : undefined,
        ai_config_id: projectCrmId || projectId,
      })

      message.success(`✅ Анализ запущен (batch #${batchResponse.batch_id})`)

      setActiveBatchId(batchResponse.batch_id)

      if (projectId) {
        setActiveAnalysis(projectId, {
          batch_id: batchResponse.batch_id,
          project_id: projectId,
          status: 'running',
          total: uniqueDomains.length + uniqueUrls.length,
          processed: 0,
          started_at: new Date().toISOString(),
        })

        try {
          saveManualWorkflow(projectId, {
            project_id: projectId,
            project_name: projectName || '',
            input_method: 'manual',
            current_step: currentStep,
            task_id: taskId || `analysis_${batchResponse.batch_id}`,
            ai_config: aiConfig,
            ai_filter: aiFilterRef.current,
            ai_search_text: aiSearchTextRef.current,
            selected_phones: Array.from(selectedPhones),
          })
        } catch (error) {
          console.warn('Failed to save workflow state (localStorage quota?):', error)
        }
      }

      // Store parsed_url_id mapping (new) or source_id (legacy)
      const urlMapping = batchResponse.url_to_parsed_url_id || batchResponse.url_to_source_id
      if (urlMapping) {
        setDomains((prevDomains) =>
          prevDomains.map((d) => {
            const url = d.full_url || (d.host.startsWith('http') ? d.host : `https://${d.host}`)
            const id = urlMapping[url]
            if (id) {
              return { ...d, parsed_url_id: id, source_id: batchResponse.url_to_source_id?.[url] }
            }
            return d
          })
        )
      }

      const batchId = batchResponse.batch_id
      let attempts = 0
      let consecutiveErrors = 0
      let lastInfoToastAt = 0
      let reconnecting = false
      const totalUrls = uniqueDomains.length + uniqueUrls.length
      const isLargeBatch = totalUrls > 1000
      const pollIntervalMs =
        totalUrls > 100000 ? 20000 : totalUrls > 10000 ? 10000 : totalUrls > 1000 ? 5000 : 3000
      const MAX_CONSECUTIVE_ERRORS = isLargeBatch ? 15 : 5
      const TOAST_THROTTLE_MS = 60000
      const maxAttempts = Math.min(
        20000,
        Math.max(240, Math.ceil(((totalUrls / 20) * 60) / (pollIntervalMs / 1000) * 2))
      )

      const loadAllResults = async () => {
        const allResults: unknown[] = []
        let currentPage = 1
        const pageSize = 200
        let hasMore = true

        while (hasMore) {
          const pageData = await api.getBatchStatus(batchId, { page: currentPage, pageSize })
          allResults.push(...pageData.results)
          hasMore = pageData.results.length === pageSize
          currentPage++
        }
        return allResults
      }

      const updateDomainsWithResults = (results: any[]) => {
        setDomains((prevDomains) => {
          const updatedDomains = [...prevDomains]
          let hasChanges = false

          const urlToIndexMap = new Map<string, number>()
          prevDomains.forEach((d, idx) => {
            const fullUrl = normalizeUrl(d.full_url || `https://${d.host}`)
            urlToIndexMap.set(fullUrl, idx)
            const hostUrl = normalizeUrl(`https://${d.host}`)
            if (hostUrl !== fullUrl) {
              urlToIndexMap.set(hostUrl, idx)
            }
          })

          results.forEach((result: any) => {
            const resultUrl = normalizeUrl(result.url)
            const index = urlToIndexMap.get(resultUrl) ?? -1

            if (index !== -1) {
              const currentStatus = updatedDomains[index].ai_status
              const newStatus = result.ai_analysis_status

              if (
                currentStatus !== newStatus ||
                (newStatus === 'completed' && !updatedDomains[index].ai_recommendation)
              ) {
                hasChanges = true

                const recommendation =
                  result.ai_recommendation ||
                  result.ai_decision ||
                  result.analysis?.recommendation ||
                  'uncertain'
                const score = result.ai_score || result.analysis?.score || 0
                const reason =
                  result.ai_reason || result.analysis?.reason || result.ai_analysis_error || ''

                updatedDomains[index] = {
                  ...updatedDomains[index],
                  ai_score: score,
                  ai_recommendation: newStatus === 'completed' ? recommendation : undefined,
                  ai_reason: reason,
                  analyzing: newStatus === 'analyzing',
                  ai_status: newStatus,
                }

                if (newStatus === 'completed' && recommendation === 'rejected') {
                  updatedDomains[index].selected = false
                }
              }
            }
          })

          return hasChanges ? updatedDomains : prevDomains
        })
      }

      // Safety: clear any stale poll
      stopAnalysisPoll()

      analysisPollRef.current = setInterval(async () => {
        try {
          attempts++

          const status = isLargeBatch
            ? await api.getBatchStatus(batchId, { includeResults: false })
            : await api.getBatchStatus(batchId)

          consecutiveErrors = 0
          if (reconnecting) {
            reconnecting = false
            message.success('🔌 Соединение восстановлено')
          }

          if (!isLargeBatch && status.results?.length > 0) {
            updateDomainsWithResults(status.results)
          }

          setBatchProgress({ processed: status.processed || 0, total: status.total || 0 })
          setQueueInfo(status.queue_info || null)

          if (projectId) {
            updateAnalysisProgress(projectId, status.processed, status.status)
          }

          if (status.status === 'running') {
            const now = Date.now()
            if (now - lastInfoToastAt >= TOAST_THROTTLE_MS) {
              lastInfoToastAt = now
              message.info(`⏳ Анализ: ${status.processed}/${status.total}`, 2)
            }
          }

          if (
            status.status === 'completed' ||
            status.status === 'failed' ||
            status.status === 'cancelled'
          ) {
            stopAnalysisPoll()

            if (isLargeBatch && (status.status === 'completed' || status.status === 'cancelled')) {
              message.info('📥 Загрузка результатов анализа...')
              try {
                const allResults = await loadAllResults()
                updateDomainsWithResults(allResults as any[])
              } catch (err) {
                console.error('Error loading results:', err)
              }
            }

            if (projectId) {
              clearActiveAnalysis(projectId)
            }

            if (status.status === 'completed' || status.status === 'cancelled') {
              const approved = status.approved || 0
              const rejected = status.rejected || 0

              if (status.status === 'cancelled') {
                message.warning(`⏸️ AI анализ отменён: ${approved} одобрено, ${rejected} отклонено`)
              } else {
                message.success(`✅ AI анализ завершен: ${approved} одобрено, ${rejected} отклонено`)
              }
            } else {
              message.error('❌ Ошибка AI анализа')
            }

            setAnalyzing(false)
            setBatchProgress(null)
          }

          if (attempts >= maxAttempts) {
            stopAnalysisPoll()
            if (projectId) {
              clearActiveAnalysis(projectId)
            }
            message.warning('⏱️ Превышено время ожидания анализа')
            setAnalyzing(false)
            setBatchProgress(null)
          }
        } catch (error) {
          consecutiveErrors++
          console.error(`Polling error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error)

          if (!reconnecting && consecutiveErrors >= 2) {
            reconnecting = true
            message.warning('🔌 Соединение нестабильно, пытаемся восстановить...', 3)
          }

          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.error('Too many consecutive polling errors, stopping local polling')
            stopAnalysisPoll()
            // ! Do NOT clearActiveAnalysis — backend keeps processing the batch.
            message.error(
              'Не удалось восстановить соединение. Анализ продолжается в фоне — закройте и откройте модалку для возобновления.',
              8
            )
            setAnalyzing(false)
            setBatchProgress(null)
          }
        }
      }, pollIntervalMs)
    } catch (error: unknown) {
      console.error('Error starting batch analysis:', error)
      const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка'
      message.error('Ошибка запуска AI анализа: ' + errMsg)
      setAnalyzing(false)
      setBatchProgress(null)
    }
  }, [
    projectId,
    projectCrmId,
    projectName,
    domains,
    currentStep,
    taskId,
    aiConfig,
    selectedPhones,
    setDomains,
    setActiveAnalysis,
    saveManualWorkflow,
    updateAnalysisProgress,
    clearActiveAnalysis,
    stopAnalysisPoll,
  ])

  const resumeAnalysisPoll = useCallback(
    async (batchId: number) => {
      // Idempotency guard
      if (analysisPollRef.current) {
        console.warn('[Workflow] Analysis polling уже запущен, пропускаем дубль')
        return
      }

      console.log(`🔄 Resuming AI analysis polling for batch #${batchId}`)

      setAnalyzing(true)

      let totalUrls = 1000
      let initialProcessed = 0
      try {
        const initialStatus = await api.getBatchStatus(batchId, { includeResults: false })
        totalUrls = initialStatus.total || 1000
        initialProcessed = initialStatus.processed || 0
        setBatchProgress({ processed: initialProcessed, total: totalUrls })
      } catch (e) {
        console.warn('Could not get initial batch status for timeout calculation')
      }

      let attempts = 0
      let consecutiveErrors = 0
      const MAX_CONSECUTIVE_ERRORS = 5
      const maxAttempts = Math.max(240, Math.ceil(((totalUrls / 20) * 60) / 3 * 2))
      console.log(
        `📊 Batch size: ${totalUrls}, maxAttempts: ${maxAttempts} (~${Math.round(
          (maxAttempts * 3) / 60
        )} min)`
      )

      analysisPollRef.current = setInterval(async () => {
        try {
          attempts++
          const status = await api.getBatchStatus(batchId)

          consecutiveErrors = 0

          setDomains((prevDomains) => {
            const updatedDomains = [...prevDomains]
            let hasChanges = false

            status.results.forEach((result: any) => {
              let index = updatedDomains.findIndex(
                (d) =>
                  (d.parsed_url_id && d.parsed_url_id === result.parsed_url_id) ||
                  (d.source_id && d.source_id === result.source_id)
              )

              if (index === -1) {
                const resultUrl = normalizeUrl(result.url)
                index = updatedDomains.findIndex((d) => {
                  const fullUrl = normalizeUrl(d.full_url || `https://${d.host}`)
                  const hostUrl = normalizeUrl(`https://${d.host}`)
                  return fullUrl === resultUrl || hostUrl === resultUrl
                })
              }

              if (index !== -1 && result.parsed_url_id && !updatedDomains[index].parsed_url_id) {
                updatedDomains[index] = {
                  ...updatedDomains[index],
                  parsed_url_id: result.parsed_url_id,
                }
                hasChanges = true
              }
              if (index !== -1 && result.source_id && !updatedDomains[index].source_id) {
                updatedDomains[index] = { ...updatedDomains[index], source_id: result.source_id }
                hasChanges = true
              }

              if (index !== -1) {
                const currentStatus = updatedDomains[index].ai_status
                const newStatus = result.ai_analysis_status

                if (
                  currentStatus !== newStatus ||
                  (newStatus === 'completed' && !updatedDomains[index].ai_recommendation)
                ) {
                  hasChanges = true
                  const recommendation =
                    result.ai_recommendation || result.ai_decision || 'uncertain'
                  const score = result.ai_score || 0
                  const reason = result.ai_reason || result.ai_analysis_error || ''

                  updatedDomains[index] = {
                    ...updatedDomains[index],
                    ai_score: score,
                    ai_recommendation: newStatus === 'completed' ? recommendation : undefined,
                    ai_reason: reason,
                    analyzing: newStatus === 'analyzing',
                    ai_status: newStatus,
                  }

                  if (newStatus === 'completed' && recommendation === 'rejected') {
                    updatedDomains[index].selected = false
                  }
                }
              }
            })

            return hasChanges ? updatedDomains : prevDomains
          })

          setBatchProgress({ processed: status.processed || 0, total: status.total || 0 })
          setQueueInfo(status.queue_info || null)

          if (projectId) {
            updateAnalysisProgress(projectId, status.processed, status.status)
          }

          if (status.status === 'running') {
            message.info(`⏳ Анализ: ${status.processed}/${status.total}`, 2)
          }

          if (
            status.status === 'completed' ||
            status.status === 'failed' ||
            status.status === 'cancelled'
          ) {
            stopAnalysisPoll()
            if (projectId) {
              clearActiveAnalysis(projectId)
            }

            if (status.status === 'completed' || status.status === 'cancelled') {
              const approved = status.approved || 0
              const rejected = status.rejected || 0
              message.success(`✅ AI анализ завершен: ${approved} одобрено, ${rejected} отклонено`)
            } else {
              message.error('❌ Ошибка AI анализа')
            }
            setAnalyzing(false)
            setBatchProgress(null)
          }

          if (attempts >= maxAttempts) {
            stopAnalysisPoll()
            if (projectId) {
              clearActiveAnalysis(projectId)
            }
            message.warning('⏱️ Превышено время ожидания анализа')
            setAnalyzing(false)
          }
        } catch (error) {
          consecutiveErrors++
          console.error(`Polling error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error)

          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.error('Too many consecutive polling errors, stopping')
            stopAnalysisPoll()
            if (projectId) {
              clearActiveAnalysis(projectId)
            }
            message.error('Потеряна связь с сервером. Откройте модалку заново для восстановления.')
            setAnalyzing(false)
          }
        }
      }, 3000)
    },
    [projectId, setDomains, updateAnalysisProgress, clearActiveAnalysis, stopAnalysisPoll]
  )

  const resetAnalysis = useCallback(
    (clearFilters: boolean = false) => {
      stopAnalysisPoll()
      setAnalyzing(false)
      setBatchProgress(null)
      setQueueInfo(null)
      setActiveBatchId(null)
      setAiAnalysisSkipped(false)
      if (clearFilters) {
        setAiFilter('all')
        setAiSearchText('')
      }
    },
    [stopAnalysisPoll]
  )

  return {
    analyzing,
    batchProgress,
    queueInfo,
    activeBatchId,
    aiAnalysisSkipped,
    aiFilter,
    aiSearchText,
    setAnalyzing,
    setBatchProgress,
    setQueueInfo,
    setActiveBatchId,
    setAiAnalysisSkipped,
    setAiFilter,
    setAiSearchText,
    handleAnalyze,
    resumeAnalysisPoll,
    stopAnalysisPoll,
    resetAnalysis,
    analysisPollRef,
  }
}
